import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api } from "../api/client";

interface Cooperatie {
  id: string;
  naam: string;
  werksoort: string | null;
}
interface Comaker {
  id: string;
  naam: string;
}
interface Template {
  id: string;
  name: string;
  description: string;
  version: string;
}
interface Werkpakket {
  werkpakket: string;
  aantal_projecten: number;
}
interface Recipient {
  email: string;
  name: string | null;
  type: string | null;
}

type Frequency = "daily" | "weekly" | "monthly";

const WEEKDAYS = [
  { value: 0, label: "maandag" },
  { value: 1, label: "dinsdag" },
  { value: 2, label: "woensdag" },
  { value: 3, label: "donderdag" },
  { value: 4, label: "vrijdag" },
  { value: 5, label: "zaterdag" },
  { value: 6, label: "zondag" },
];

interface Props {
  onClose: () => void;
  onCreated: () => void;
}

export default function ScheduleFormModal({ onClose, onCreated }: Props) {
  const [templateIds, setTemplateIds] = useState<string[]>(["rgs_samenwerking"]);
  const [coopId, setCoopId] = useState<string>("");
  const [comakerIds, setComakerIds] = useState<string[]>([]);
  const [werkpakketten, setWerkpakketten] = useState<string[]>([]);

  const [frequency, setFrequency] = useState<Frequency>("weekly");
  const [hour, setHour] = useState(8);
  const [minute, setMinute] = useState(0);
  const [weekday, setWeekday] = useState(0);
  const [dayOfMonth, setDayOfMonth] = useState(1);

  const [recipientEmails, setRecipientEmails] = useState<string[]>([]);

  const templates = useQuery({
    queryKey: ["templates"],
    queryFn: async () => (await api.get<Template[]>("/templates")).data,
  });
  const coops = useQuery({
    queryKey: ["cooperaties"],
    queryFn: async () => (await api.get<Cooperatie[]>("/cooperaties")).data,
  });
  // Gebruikers met toegang tot precies één coöperatie hoeven niet te kiezen.
  useEffect(() => {
    if (!coopId && coops.data?.length === 1) setCoopId(coops.data[0].id);
  }, [coopId, coops.data]);
  const comakers = useQuery({
    queryKey: ["comakers", coopId],
    queryFn: async () =>
      (await api.get<Comaker[]>("/comakers", { params: { cooperatie_id: coopId } })).data,
    enabled: !!coopId,
  });
  const werkpakkettenQuery = useQuery({
    queryKey: ["werkpakketten", coopId],
    queryFn: async () =>
      (await api.get<Werkpakket[]>("/werkpakketten", { params: { cooperatie_id: coopId } })).data,
    enabled: !!coopId,
  });
  const recipients = useQuery({
    queryKey: ["recipients", coopId, comakerIds],
    queryFn: async () =>
      (
        await api.get<Recipient[]>("/recipients", {
          params: {
            cooperatie_id: coopId,
            comaker_ids: comakerIds.join(",") || undefined,
          },
        })
      ).data,
    enabled: !!coopId,
  });

  // Naam volgt uit de gekozen ontvanger (lookup); niet meer vrij invulbaar.
  const selectedRecipients = recipientEmails.map((email) => ({
    email,
    name: recipients.data?.find((r) => r.email === email)?.name ?? null,
  }));

  const create = useMutation({
    mutationFn: async () => {
      const payload = {
        template_ids: templateIds,
        cooperatie_id: coopId,
        comaker_ids: comakerIds,
        werkpakketten,
        frequency,
        hour,
        minute,
        weekday: frequency === "weekly" ? weekday : null,
        day_of_month: frequency === "monthly" ? dayOfMonth : null,
        recipients: selectedRecipients,
      };
      const r = await api.post("/schedules", payload);
      return r.data;
    },
    onSuccess: () => onCreated(),
  });

  const toggleTemplate = (id: string) =>
    setTemplateIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  const toggleComaker = (id: string) =>
    setComakerIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  const toggleWerkpakket = (wp: string) =>
    setWerkpakketten((prev) =>
      prev.includes(wp) ? prev.filter((x) => x !== wp) : [...prev, wp],
    );
  const toggleRecipient = (email: string) =>
    setRecipientEmails((prev) =>
      prev.includes(email) ? prev.filter((x) => x !== email) : [...prev, email],
    );

  const canSubmit =
    templateIds.length > 0 && !!coopId && recipientEmails.length > 0 && !create.isPending;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 px-4 py-10"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-3xl rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 className="text-lg font-semibold text-kbs-primary">Nieuwe schedule</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
            aria-label="Sluit"
          >
            ✕
          </button>
        </div>

        <div className="space-y-5 px-6 py-5">
          <Field label="Templates (vink één of meer aan)">
            {templates.isLoading ? (
              <p className="text-sm text-slate-500">Laden…</p>
            ) : (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {templates.data?.map((t) => (
                  <label
                    key={t.id}
                    className="flex cursor-pointer items-start gap-2 rounded border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50"
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={templateIds.includes(t.id)}
                      onChange={() => toggleTemplate(t.id)}
                    />
                    <span>
                      <span className="font-medium">{t.name}</span>
                      {t.description && (
                        <span className="block text-xs text-slate-500">{t.description}</span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            )}
            {templateIds.length === 0 && (
              <p className="mt-1 text-xs text-rose-600">Kies minstens één template.</p>
            )}
          </Field>

          <Field label="Coöperatie">
            {coops.isLoading ? (
              <p className="text-sm text-slate-500">Laden…</p>
            ) : (
              <select
                className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                value={coopId}
                onChange={(e) => {
                  setCoopId(e.target.value);
                  setComakerIds([]);
                  setWerkpakketten([]);
                  setRecipientEmails([]);
                }}
              >
                <option value="">— Kies een Coöperatie —</option>
                {coops.data?.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.naam}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label="Co-makers (optioneel — leeg = alle)">
            {!coopId && <p className="text-sm text-slate-400">Kies eerst een Coöperatie.</p>}
            {coopId && comakers.isLoading && <p className="text-sm text-slate-500">Laden…</p>}
            {comakers.data && (
              <div className="grid grid-cols-2 gap-2">
                {comakers.data.map((c) => (
                  <label
                    key={c.id}
                    className="flex cursor-pointer items-center gap-2 rounded border border-slate-200 px-3 py-2 text-sm hover:bg-slate-50"
                  >
                    <input
                      type="checkbox"
                      checked={comakerIds.includes(c.id)}
                      onChange={() => {
                        toggleComaker(c.id);
                        setRecipientEmails([]);
                      }}
                    />
                    <span>{c.naam}</span>
                  </label>
                ))}
              </div>
            )}
          </Field>

          <Field label="Werkpakketten / jaren (optioneel — leeg = alle jaren)">
            {!coopId && <p className="text-sm text-slate-400">Kies eerst een Coöperatie.</p>}
            {coopId && werkpakkettenQuery.isLoading && (
              <p className="text-sm text-slate-500">Laden…</p>
            )}
            {werkpakkettenQuery.data && werkpakkettenQuery.data.length === 0 && (
              <p className="text-sm text-slate-400">
                Geen werkpakket-jaartallen aanwezig voor deze Coöperatie.
              </p>
            )}
            {werkpakkettenQuery.data && werkpakkettenQuery.data.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {werkpakkettenQuery.data.map((w) => {
                  const active = werkpakketten.includes(w.werkpakket);
                  return (
                    <button
                      type="button"
                      key={w.werkpakket}
                      onClick={() => toggleWerkpakket(w.werkpakket)}
                      className={
                        "rounded-full border px-3 py-1 text-sm transition " +
                        (active
                          ? "border-kbs-primary bg-kbs-primary text-white"
                          : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50")
                      }
                    >
                      {w.werkpakket}
                      <span className="ml-1 text-xs opacity-70">({w.aantal_projecten})</span>
                    </button>
                  );
                })}
              </div>
            )}
          </Field>

          <div className="rounded border border-slate-200 bg-slate-50 p-4">
            <h3 className="mb-3 text-sm font-semibold text-kbs-primary">
              Wanneer moet dit rapport draaien?
            </h3>

            <Field label="Frequentie">
              <div className="flex gap-2">
                {(["daily", "weekly", "monthly"] as Frequency[]).map((f) => (
                  <button
                    type="button"
                    key={f}
                    onClick={() => setFrequency(f)}
                    className={
                      "flex-1 rounded border px-3 py-2 text-sm capitalize transition " +
                      (frequency === f
                        ? "border-kbs-primary bg-kbs-primary text-white"
                        : "border-slate-300 bg-white text-slate-700 hover:bg-slate-100")
                    }
                  >
                    {f === "daily" ? "Dagelijks" : f === "weekly" ? "Wekelijks" : "Maandelijks"}
                  </button>
                ))}
              </div>
            </Field>

            {frequency === "weekly" && (
              <Field label="Op welke dag?">
                <select
                  className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
                  value={weekday}
                  onChange={(e) => setWeekday(Number(e.target.value))}
                >
                  {WEEKDAYS.map((d) => (
                    <option key={d.value} value={d.value}>
                      {d.label}
                    </option>
                  ))}
                </select>
              </Field>
            )}

            {frequency === "monthly" && (
              <Field label="Op welke dag van de maand? (1–28)">
                <input
                  type="number"
                  min={1}
                  max={28}
                  value={dayOfMonth}
                  onChange={(e) => setDayOfMonth(Number(e.target.value))}
                  className="w-32 rounded border border-slate-300 px-3 py-2 text-sm"
                />
              </Field>
            )}

            <Field label="Tijd (Europe/Amsterdam)">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  max={23}
                  value={hour}
                  onChange={(e) => setHour(Number(e.target.value))}
                  className="w-20 rounded border border-slate-300 px-3 py-2 text-sm"
                />
                <span className="text-slate-500">:</span>
                <input
                  type="number"
                  min={0}
                  max={59}
                  value={minute}
                  onChange={(e) => setMinute(Number(e.target.value))}
                  className="w-20 rounded border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
            </Field>
          </div>

          <div className="rounded border border-slate-200 bg-slate-50 p-4">
            <h3 className="mb-3 text-sm font-semibold text-kbs-primary">
              Naar wie sturen we dit?
            </h3>
            <Field label="Ontvangers (vink één of meer KBS-contacten aan)">
              {!coopId && <p className="text-sm text-slate-400">Kies eerst een Coöperatie.</p>}
              {coopId && recipients.isLoading && (
                <p className="text-sm text-slate-500">Laden…</p>
              )}
              {coopId && recipients.data && recipients.data.length === 0 && (
                <p className="text-sm text-slate-400">
                  Geen KBS-contacten bekend voor deze omgeving. Er kan alleen naar
                  contacten worden gestuurd die bij deze coöperatie/comaker horen.
                </p>
              )}
              {recipients.data && recipients.data.length > 0 && (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {recipients.data.map((r) => (
                    <label
                      key={r.email}
                      className="flex cursor-pointer items-start gap-2 rounded border border-slate-200 bg-white px-3 py-2 text-sm hover:bg-slate-50"
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={recipientEmails.includes(r.email)}
                        onChange={() => toggleRecipient(r.email)}
                      />
                      <span>
                        {r.name && <span className="font-medium">{r.name}</span>}
                        <span className="block text-xs text-slate-500">{r.email}</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
              {coopId && recipients.data && recipients.data.length > 0 && recipientEmails.length === 0 && (
                <p className="mt-1 text-xs text-rose-600">Kies minstens één ontvanger.</p>
              )}
              <p className="mt-1 text-xs text-slate-500">
                Het rapport wordt per e-mail (Azure Communication Services) aan alle
                gekozen ontvangers bezorgd en verschijnt ook bij Recente rapporten.
              </p>
            </Field>
          </div>

          {create.error && (
            <p className="text-sm text-rose-600">
              Fout bij opslaan: {(create.error as Error).message}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            Annuleer
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => create.mutate()}
            className="rounded bg-kbs-primary px-5 py-2 text-sm font-semibold text-white hover:bg-kbs-dark disabled:opacity-50"
          >
            {create.isPending ? "Opslaan…" : "Opslaan"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-3 last:mb-0">
      <label className="mb-1 block text-sm font-medium text-kbs-primary">{label}</label>
      {children}
    </div>
  );
}
